// src/utils/taskUtils.js
import { TASK_PRIORITY_ORDER } from '../constants/task';

/**
 * Filter tasks based on combined filter criteria.
 * All active filters are AND-combined.
 */
export function filterTasks(tasks, filters) {
  const { search, status, priority, assignee } = filters;

  return tasks.filter((task) => {
    if (search && search.trim()) {
      const searchLower = search.toLowerCase().trim();
      if (!task.title.toLowerCase().includes(searchLower)) return false;
    }

    if (status && status.length > 0) {
      if (!status.includes(task.status)) return false;
    }

    if (priority && priority.length > 0) {
      if (!priority.includes(task.priority)) return false;
    }

    if (assignee && assignee.length > 0) {
      if (!assignee.includes(task.assignee)) return false;
    }

    return true;
  });
}

/**
 * Group tasks by their status.
 */
export function groupTasksByStatus(tasks, statusOrder) {
  return statusOrder.reduce((acc, status) => {
    acc[status] = tasks.filter((task) => task.status === status);
    return acc;
  }, {});
}

/**
 * Sort tasks within a column by priority (descending) then by creation date.
 */
export function sortTasksByPriority(tasks) {
  return [...tasks].sort((a, b) => {
    const priorityDiff =
      (TASK_PRIORITY_ORDER[b.priority] ?? 0) - (TASK_PRIORITY_ORDER[a.priority] ?? 0);
    if (priorityDiff !== 0) return priorityDiff;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
}

/**
 * Validate a task form payload.
 * Returns an object of field -> error message. Empty object means valid.
 */
export function validateTask(values) {
  const errors = {};

  if (!values.title || !values.title.trim()) {
    errors.title = 'Title is required.';
  } else if (values.title.trim().length > 120) {
    errors.title = 'Title must be 120 characters or fewer.';
  }

  if (!values.priority) {
    errors.priority = 'Priority is required.';
  }

  if (values.dueDate) {
    const due = new Date(values.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (due < today) {
      errors.dueDate = 'Due date cannot be in the past.';
    }
  }

  return errors;
}

/**
 * Get initials from a full name string.
 */
export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Format an ISO date string to a human-friendly label.
 */
export function formatDueDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((date - today) / (1000 * 60 * 60 * 24));

  if (diff < 0) return { label: 'Overdue', overdue: true };
  if (diff === 0) return { label: 'Due today', urgent: true };
  if (diff === 1) return { label: 'Due tomorrow', urgent: true };
  return {
    label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    overdue: false,
    urgent: false,
  };
}

/**
 * Check if filters object has any active filters.
 */
export function hasActiveFilters(filters) {
  return (
    (filters.search && filters.search.trim().length > 0) ||
    (filters.status && filters.status.length > 0) ||
    (filters.priority && filters.priority.length > 0) ||
    (filters.assignee && filters.assignee.length > 0)
  );
}
